import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Info, Send } from "lucide-react";
import { WhatsAppIcon } from "@/components/Icon";
import { SERVICES } from "@/data/services";
import { ALL_INDUSTRIES } from "@/data/industries";
import { BUDGETS } from "@/data/home";
import { WA_DEFAULT } from "@/data/site";

// CRM lead capture endpoint. Requests use Content-Type text/plain so the browser
// sends a "simple" request (no CORS preflight) and the lead works from any domain.
const CRM_ENDPOINT = "https://leadcrm.ashawebtechitsolutions.com/api/v1/leads";
const CRM_KEY = "lhk_ab4907e89bed51e2fafa7a9551e44ffc7ce6697d";

const countDigits = (v) => (v.match(/\d/g) || []).length;

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your full name.").max(100),
  email: z.string().trim().email("Please enter a valid email address.").max(150),
  phone: z
    .string()
    .trim()
    .min(1, "Please enter your phone number.")
    .max(30)
    .regex(/^[+\d\s()-]*$/, "Use digits, spaces, + or - only.")
    .refine((v) => countDigits(v) >= 7, "Please enter a valid phone number."),
  company: z.string().trim().max(120).optional(),
  country: z.string().trim().max(80).optional(),
  industry: z.string().optional(),
  service: z.string().optional(),
  budget: z.string().optional(),
  details: z.string().trim().max(1800, "Please keep details under 1,800 characters.").optional(),
  contact: z.enum(["WhatsApp", "Email"]),
});

const DEFAULTS = { name: "", email: "", phone: "", company: "", country: "", industry: "", service: "", budget: "", details: "", contact: "WhatsApp" };

// Collects every named control in the form, keyed by its own name attribute.
const collectFields = (form) => {
  const fields = {};
  for (const el of Array.from(form.elements)) {
    if (!el.name || el.disabled) continue;
    if (el.type === "radio") {
      if (el.checked) fields[el.name] = el.value;
      else if (!(el.name in fields)) fields[el.name] = "";
      continue;
    }
    fields[el.name] = el.value ?? "";
  }
  return fields;
};

const Field = ({ id, label, required, error, children, hint }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-[13px] font-semibold text-charcoal">
      {label} {required ? <span className="text-rust-ink" aria-hidden="true">*</span> : <span className="font-normal text-graphite">(optional)</span>}
    </label>
    {children}
    {hint && !error && <p className="mt-1.5 text-[12px] text-graphite">{hint}</p>}
    {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-[13px] font-medium text-[#A33A1E]" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

export const InquiryForm = ({ compact = false }) => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const formRef = useRef(null);
  const thanksRef = useRef(null);
  const attempted = useRef(false);

  const { register, handleSubmit, reset, trigger, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    const ind = params.get("industry");
    const svc = params.get("service");
    if (ind) setValue("industry", ind);
    if (svc) setValue("service", svc);
  }, [params, setValue]);

  useEffect(() => {
    if (status === "success") thanksRef.current?.focus();
  }, [status]);

  const onValid = async () => {
    setStatus("sending");
    const payload = {
      key: CRM_KEY,
      fields: collectFields(formRef.current),
      source_url: window.location.href,
      page_title: document.title,
    };
    try {
      const res = await fetch(CRM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (json && json.ok === true) {
        reset(DEFAULTS);
        attempted.current = false;
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleSubmit(onValid, () => {
      attempted.current = true;
      document.querySelector("[aria-invalid=true]")?.focus();
    })(e);
  };

  const reg = (k) => register(k, { onChange: () => { if (attempted.current) trigger(k); } });
  const a = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });
  const contact = watch("contact");

  if (status === "success") {
    return (
      <div
        ref={thanksRef}
        tabIndex={-1}
        role="status"
        className="rounded-md border border-sage/50 bg-[#EEF0E8] p-8 text-charcoal outline-none sm:p-10"
        data-testid="form-thank-you"
      >
        <CheckCircle2 className="h-10 w-10 text-sage" aria-hidden="true" />
        <p className="mt-5 font-display text-2xl font-bold">Thank you for your enquiry!</p>
        <p className="mt-3 text-[15px] leading-relaxed text-graphite">
          We have received your details. Our team will review your requirements and get back to you shortly.
        </p>
        <button type="button" onClick={() => setStatus("idle")} className="btn-ghost mt-8 bg-white" data-testid="form-send-another">
          Send another enquiry
        </button>
      </div>
    );
  }

  const sending = isSubmitting || status === "sending";

  return (
    <form ref={formRef} noValidate onSubmit={onSubmit} className="grid gap-5" data-testid="inquiry-form" aria-label="Project inquiry form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label="Full Name" required error={errors.name?.message}>
          <input id="name" autoComplete="name" className="field" placeholder="e.g. Priya Sharma" {...reg("name")} {...a("name")} data-testid="input-name" />
        </Field>
        <Field id="email" label="Email" required error={errors.email?.message}>
          <input id="email" type="email" autoComplete="email" className="field" placeholder="name@company.com" {...reg("email")} {...a("email")} data-testid="input-email" />
        </Field>
        <Field id="phone" label="Phone / WhatsApp" required error={errors.phone?.message}>
          <input id="phone" type="tel" autoComplete="tel" className="field" placeholder="+91 00000 00000" {...reg("phone")} {...a("phone")} data-testid="input-phone" />
        </Field>
        <Field id="company" label="Company Name" error={errors.company?.message}>
          <input id="company" autoComplete="organization" className="field" placeholder="Your company" {...reg("company")} data-testid="input-company" />
        </Field>
        <Field id="country" label="Country" error={errors.country?.message}>
          <input id="country" autoComplete="country-name" className="field" placeholder="e.g. United Kingdom" {...reg("country")} data-testid="input-country" />
        </Field>
        <Field id="industry" label="Industry">
          <select id="industry" className="field" {...reg("industry")} data-testid="select-industry">
            <option value="">Select your industry</option>
            {ALL_INDUSTRIES.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
            <option value="Other">Other</option>
          </select>
        </Field>
        <Field id="service" label="Service Required">
          <select id="service" className="field" {...reg("service")} data-testid="select-service">
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
            <option value="Not sure yet">Not sure yet, need advice</option>
          </select>
        </Field>
        <Field id="budget" label="Project Budget">
          <select id="budget" className="field" {...reg("budget")} data-testid="select-budget">
            <option value="">Select a range</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
      </div>
      <Field id="details" label="Project Details" error={errors.details?.message} hint="Your current process, challenges, number of users, systems in use and timeline.">
        <textarea id="details" rows={compact ? 4 : 6} className="field resize-y" placeholder="Tell us what you would like to improve, which systems you use today, and any timelines." {...reg("details")} {...a("details")} data-testid="input-details" />
      </Field>
      <fieldset>
        <legend className="mb-2 text-[13px] font-semibold text-charcoal">Preferred Contact Method</legend>
        <div className="flex flex-wrap gap-3">
          {["WhatsApp", "Email"].map((m) => (
            <label key={m} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border px-5 text-[14px] transition-colors ${contact === m ? "border-charcoal bg-charcoal text-ivory" : "border-[#CFCABE] bg-white text-charcoal"}`}>
              <input type="radio" value={m} className="sr-only" {...reg("contact")} data-testid={`radio-contact-${m.toLowerCase()}`} />
              {m}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="rounded-md border border-stone bg-ivory p-5">
        <p className="flex gap-3 text-[13px] leading-relaxed text-graphite" data-testid="form-privacy-notice">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-rust-ink" aria-hidden="true" />
          <span>Your details are used only to respond to your enquiry. Please read our <Link to="/privacy-policy" className="underline underline-offset-4">Privacy Policy</Link>.</span>
        </p>
        {status === "error" && (
          <div role="alert" className="mt-5 flex gap-3 rounded-md border border-[#E7B9AC] bg-[#FBEDE8] p-4 text-[14px] leading-relaxed text-[#7A2A16]" data-testid="form-error">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              We could not send your enquiry right now. Please check your connection and try again, or <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-4">message us on WhatsApp</a>.
            </span>
          </div>
        )}
        <div className="mt-5">
          <button type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid="submit-enquiry-btn" aria-busy={sending}>
            {sending ? "Sending…" : <><Send className="h-4 w-4" aria-hidden="true" /> Send Enquiry</>}
          </button>
          <a href={WA_DEFAULT} target="_blank" rel="noopener noreferrer" className="btn-ghost ml-0 mt-3 w-full bg-white sm:ml-3 sm:mt-0 sm:w-auto" data-testid="submit-whatsapp-btn">
            <WhatsAppIcon className="h-4 w-4" aria-hidden="true" /> Chat on WhatsApp
          </a>
        </div>
      </div>
    </form>
  );
};
